{
  "title": "How to check in riders and use the passenger list on event day",
  "description": "Staff use the shuttle check-in page to scan ride QR codes, including offline queueing, and cross-check against the passenger list.",
  "headings": [
    "What you will accomplish",
    "Who this is for",
    "Before you start",
    "Steps",
    "Afterward",
    "FAQ"
  ],
  "summary": "When done every boarding has a check-in record, duplicates and wrong-trip scans are logged too, and you can queue scans offline and sync them later.",
  "roleNote": "Event staff: Scanner and Owner can check in and see the passenger list (checkin and manifest capabilities); Manager and Viewer cannot check in",
  "prerequisites": [
    "You were added as a member of the event and your account email is verified (someone added before registering shows as pending and becomes active after registering and verifying)",
    "A device that supports camera scanning (BarcodeDetector) is easier; otherwise paste the credential",
    "Know the trip codes in advance, for example 113 is day one from NTPU to the venue"
  ],
  "pathLabel": "Event page → Staff and attendees → Shuttle check-in (/events/:slug/staff/check-in) and Passenger list (/events/:slug/staff/roster)",
  "steps": [
    {
      "title": "1. Pick the current trip",
      "description": "Open Shuttle check-in and choose the trip in Current trip. The menu shows \"code · departure time · from-to stations\" so it is not just digits. Each booking can be checked in successfully once, on its own trip."
    },
    {
      "title": "2. Scan the ride QR",
      "description": "Press Start camera scan and point at the rider's QR, or paste the credential and press Check in. The result panel uses icon, text and colour: check-in successful, duplicate scan, wrong trip, invalid credential, or booking not confirmed (waitlisted or cancelled). The page counts Checked in this session, counting only server-confirmed successes. The result shows the rider's name only, never an email or other personal data."
    },
    {
      "title": "3. Handle exceptions manually",
      "description": "A duplicate, wrong-trip or invalid result is marked Needs manual handling. Ask the rider for ID and cross-check the name in the Passenger list; companions appear under their own names. These non-success results are kept as audit records."
    },
    {
      "title": "4. Queue scans when offline",
      "description": "Without a network, scans are held on this device and marked \"Saved offline (pending sync, server result prevails)\"; that is not a successful check-in. When the connection returns press Sync now. Up to 200 scans sync at once and the page reports how many synced and how many were not successes. If several devices scan the same QR the server still accepts only one success."
    },
    {
      "title": "5. Cross-check with the passenger list",
      "description": "Open Passenger list, pick the trip, then use \"Search name or organization\" and the status filter (all, not checked in, checked in, waitlisted). The page shows \"Checked in X / Y\" and the number shown. The list has name, organization, job title and status, never mobile numbers. Only an Owner can press \"Show emergency contact phones\"; every load is written to the audit log, so use it only for urgent contact."
    }
  ],
  "afterward": [
    "The overview KPIs on the organizer console reflect the checked-in count",
    "Do not clear browser data while offline scans are still unsynced"
  ],
  "nextSteps": "For member roles and console settings see \"How to manage event members and the organizer console\".",
  "warning": "An offline entry is only provisional. Sync as soon as the network returns and trust the server result.",
  "faq": [
    {
      "q": "What if a scan says wrong trip?",
      "a": "Check that the selected trip matches the rider's pass. If they really took another trip, on-site staff handle it manually; the system never counts it as a success."
    },
    {
      "q": "Can waitlisted riders board?",
      "a": "A waitlist entry has no QR and is not a confirmed seat. After someone cancels, promotion is automatic with a notification and only then does a scannable QR exist."
    },
    {
      "q": "I cannot see check-in or the list.",
      "a": "Your event role lacks the capability. Only Scanner and Owner have check-in and the passenger list; ask the event Owner."
    }
  ],
  "figures": [
    {
      "name": "check-in",
      "alt": "Shuttle check-in: pick the trip, scan or paste the credential; the result panel shows success and the session count",
      "caption": "Shuttle check-in: pick the trip, scan or paste the credential; the result panel shows success and the session count"
    },
    {
      "name": "roster",
      "alt": "Passenger list: search and filter not-checked-in and waitlisted riders, with no phone numbers",
      "caption": "Passenger list: search and filter not-checked-in and waitlisted riders, with no phone numbers"
    }
  ]
}
